import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import { Pagination } from '../../navigation/Pagination';
import { buildBandSegments } from './band-segments';
import {
  buildCsv,
  buildExcel,
  downloadBlob,
  ExportTooLargeError,
  formatExportFileName,
} from './export';
import {
  buildGroupTree,
  buildVisualRows,
  collectAllPaths,
  type VisualRow,
} from './grouping';
import { compareValues, readSortValue } from './sort';
import { DataGridBody } from './DataGridBody';
import { DataGridHeader } from './DataGridHeader';
import { DataGridToolbar } from './DataGridToolbar';
import { GrandTotalRow } from './rows/GrandTotalRow';
import type {
  DataGridColumn,
  DataGridExport,
  DataGridProps,
  DataGridSortState,
} from './types';

/**
 * Generic, headless-ish data grid backed by plain HTML table semantics
 * and Tailwind styling. This file is the **orchestrator** — it owns
 * state (sort, expand, export), derives memoized data (sortedData,
 * groupTree, visualRows), and dispatches rendering to sibling modules :
 *
 *   - `DataGridToolbar`  : title + expand/collapse + export dropdown
 *   - `DataGridHeader`   : <thead> bands + main header row + sort buttons
 *   - `DataGridBody`     : <tbody> with virtualization + row dispatch
 *   - `GrandTotalRow`    : <tfoot> with sticky grand total
 *
 * Anything that has to do with formatting or layout lives in those
 * files, or in `column-types.ts` / `cell-content.ts` for the single
 * source of truth on display + export rules.
 *
 * @see ./CLAUDE.md for the full architecture + conventions walkthrough.
 */
export function DataGrid<T>({
  data,
  columns,
  bands,
  rowKey,
  isLoading = false,
  skeletonRows = 6,
  emptyState,
  onRowClick,
  sortState,
  onSortChange,
  defaultSort,
  pagination,
  grouping,
  summary,
  showGrandTotal = true,
  title,
  exportable,
  currency = 'EUR',
  className,
  density = 'comfortable',
  singleLineRows = false,
  stickyHeader = false,
  maxHeight,
}: DataGridProps<T>) {
  // ---------- Sort state --------------------------------------------------
  const isControlledSort = sortState !== undefined;
  const [internalSort, setInternalSort] = useState<DataGridSortState | null>(null);
  const effectiveSort = isControlledSort ? sortState : internalSort;

  const handleSortClick = (column: DataGridColumn<T>) => {
    if (column.sortable === false) return;
    if (column.sortable !== true && !column.accessor && !column.sortValue) return;

    // Tri-state cycle : unsorted → asc → desc → unsorted (back to defaultSort)
    const current = effectiveSort;
    let next: DataGridSortState | null;
    if (!current || current.columnId !== column.id) {
      next = { columnId: column.id, direction: 'asc' };
    } else if (current.direction === 'asc') {
      next = { columnId: column.id, direction: 'desc' };
    } else {
      next = null;
    }
    if (isControlledSort) onSortChange?.(next);
    else setInternalSort(next);
  };

  // ---------- Sorted data -------------------------------------------------
  //
  // Priority : server-controlled → header click → defaultSort → raw.
  // Grouping kicks in AFTER this step, so this ordering drives the
  // group tree's insertion order.
  //
  const sortedData = useMemo(() => {
    if (!data) return data;
    if (isControlledSort) return data;

    if (effectiveSort) {
      const column = columns.find((c) => c.id === effectiveSort.columnId);
      if (!column) return data;
      const direction = effectiveSort.direction;
      return [...data].sort((a, b) =>
        compareValues(readSortValue(column, a), readSortValue(column, b), direction)
      );
    }

    if (defaultSort && defaultSort.length > 0) {
      return [...data].sort((a, b) => {
        for (const key of defaultSort) {
          const accessor = typeof key === 'function' ? key : key.value;
          const direction =
            typeof key === 'function' ? 'asc' : (key.direction ?? 'asc');
          const cmp = compareValues(accessor(a), accessor(b), direction);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
    }

    return data;
  }, [data, effectiveSort, isControlledSort, columns, defaultSort]);

  // ---------- Grouping + expand state -------------------------------------
  const hasSummary = !!summary && summary.length > 0;

  const groupTree = useMemo(() => {
    if (!grouping || !sortedData || sortedData.length === 0) return null;
    return buildGroupTree(sortedData, grouping);
  }, [grouping, sortedData]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Reset / seed the expand set when the tree shape changes. Preserve
  // previously-open paths that still exist — avoids collapsing
  // everything when the user re-sorts or filters upstream.
  useEffect(() => {
    if (!groupTree) return;
    const all = collectAllPaths(groupTree);
    const allSet = new Set(all);
    const defaultMode = grouping?.defaultExpanded ?? 'all';
    setExpanded((prev) => {
      const next = new Set<string>();
      if (defaultMode === 'all') for (const key of all) next.add(key);
      for (const key of prev) if (allSet.has(key)) next.add(key);
      return next;
    });
    // `grouping?.defaultExpanded` is intentionally excluded — we only
    // reseed when the tree itself changes shape, never on prop-only edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupTree]);

  const toggleGroup = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allPaths = useMemo(
    () => (groupTree ? collectAllPaths(groupTree) : []),
    [groupTree]
  );
  const hasAnyGroup = allPaths.length > 0;
  const allExpanded = hasAnyGroup && expanded.size === allPaths.length;
  const allCollapsed = hasAnyGroup && expanded.size === 0;

  const expandAll = () => setExpanded(new Set(allPaths));
  const collapseAll = () => setExpanded(new Set());

  // ---------- Visual row stream -------------------------------------------
  //
  // Unified array of `VisualRow` whether the grid is grouped or not.
  // Flat (ungrouped) rows get wrapped as `{ kind: 'data' }` so the
  // virtualizer and row dispatcher have a single code path.
  //
  const visualRows = useMemo<VisualRow<T>[] | null>(() => {
    if (!groupTree || !sortedData) return null;
    return buildVisualRows(groupTree, expanded, hasSummary);
  }, [groupTree, sortedData, expanded, hasSummary]);

  const renderableRows = useMemo<VisualRow<T>[] | null>(() => {
    if (visualRows) return visualRows;
    if (!sortedData) return null;
    return sortedData.map((row, index) => ({ kind: 'data' as const, row, index }));
  }, [visualRows, sortedData]);

  // ---------- Virtualization ---------------------------------------------
  const scrollRef = useRef<HTMLDivElement>(null);

  // Virtualizer is always instantiated but only consumed when `maxHeight`
  // is set — virtualizing a grid that scrolls with the page would
  // require window-level virtualization (much more complexity for
  // marginal gain on BStock/BStaff reports).
  const virtualizer = useVirtualizer({
    count: renderableRows?.length ?? 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => (density === 'compact' ? 36 : 44),
    overscan: 12,
  });
  const isVirtualized =
    !!maxHeight && !!renderableRows && renderableRows.length > 0;
  const virtualItems = isVirtualized ? virtualizer.getVirtualItems() : null;
  const virtualTotalSize = virtualizer.getTotalSize();
  const virtualPaddingTop =
    virtualItems && virtualItems.length > 0 ? virtualItems[0].start : 0;
  const virtualPaddingBottom =
    virtualItems && virtualItems.length > 0
      ? virtualTotalSize - virtualItems[virtualItems.length - 1].end
      : 0;

  // ---------- Export ------------------------------------------------------
  //
  // Currency fallback chain : `exportable.currency` → grid-level
  // `currency` → `'EUR'`. Keeps display and export in sync by default
  // while still letting callers override the export format if needed.
  //
  const [isExporting, setIsExporting] = useState(false);
  const exportConfig = useMemo(() => {
    if (!exportable) return null;
    const raw: DataGridExport<T> =
      typeof exportable === 'object' ? exportable : {};
    return {
      fileName: formatExportFileName(raw.fileName),
      sheetName: raw.sheetName ?? 'Export',
      csv: raw.csv !== false,
      excel: raw.excel !== false,
      currency: raw.currency ?? currency,
      onFetchAllRows: raw.onFetchAllRows,
    };
  }, [exportable, currency]);

  /**
   * Resout les lignes a serializer dans le fichier exporte :
   * - si `onFetchAllRows` est fourni -> appel API avec `limit = EXPORT_LIMIT_MAX`
   *   (le helper `fetchAllForExport` cote consumer fait la verif total/items
   *   et lance `ExportTooLargeError` si la liste depasse la limite). Sinon,
   *   on tomberait sur `sortedData` qui est la page courante uniquement.
   * - sinon -> on prend `sortedData` (cas des tables qui chargent tout
   *   d'un coup, type rapports, ou des petites tables non paginees).
   *
   * Erreurs `ExportTooLargeError` -> toast.error + return null pour annuler
   * l'export proprement. Autres erreurs -> rethrow (laisse remonter).
   */
  const resolveExportRows = async (): Promise<T[] | null> => {
    if (!exportConfig?.onFetchAllRows) {
      return sortedData ? [...sortedData] : null;
    }
    try {
      return await exportConfig.onFetchAllRows();
    } catch (err) {
      if (err instanceof ExportTooLargeError) {
        toast.error(err.message, { id: 'datagrid-export-too-large' });
        return null;
      }
      throw err;
    }
  };

  const handleExportCsv = async () => {
    if (!exportConfig) return;
    setIsExporting(true);
    try {
      const rows = await resolveExportRows();
      if (!rows) return;
      const blob = buildCsv(columns, rows);
      await downloadBlob(blob, `${exportConfig.fileName}.csv`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!exportConfig) return;
    setIsExporting(true);
    try {
      const rows = await resolveExportRows();
      if (!rows) return;
      const blob = await buildExcel({
        columns,
        rows,
        sheetName: exportConfig.sheetName,
        currency: exportConfig.currency,
        grouping,
        summary,
        showGrandTotal,
      });
      await downloadBlob(blob, `${exportConfig.fileName}.xlsx`);
    } finally {
      setIsExporting(false);
    }
  };

  // ---------- Layout derivations -----------------------------------------
  const exportPosition = (
    typeof exportable === 'object' ? exportable?.position : undefined
  ) ?? 'toolbar';
  const exportInHeader = exportPosition === 'header';

  const bandSegments = useMemo(
    () => buildBandSegments(columns, bands),
    [columns, bands]
  );
  const hasBands = bandSegments !== null && bandSegments.some((s) => s.band !== null);

  const isEmpty = !isLoading && Array.isArray(sortedData) && sortedData.length === 0;
  const hasRows = Array.isArray(sortedData) && sortedData.length > 0;

  // The toolbar bar is shown when there's a title, grouping controls, or
  // an export dropdown in toolbar position. When export lives in the header
  // (`position: 'header'`), it doesn't count toward toolbar visibility.
  const exportInToolbar = !!exportConfig && !exportInHeader;
  const showHeaderBar = !!title || exportInToolbar || hasAnyGroup;
  const canExport =
    !!exportConfig && !!sortedData && sortedData.length > 0 && !isLoading;

  // `maxHeight` implies an internal vertical scroll, which in turn
  // means the header row must stick to the top of that container.
  const scrollContainerStyle =
    maxHeight !== undefined
      ? { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }
      : undefined;
  const effectiveStickyHeader = stickyHeader || maxHeight !== undefined;

  // ---------- Render -----------------------------------------------------
  return (
    <div className={cn('space-y-4', className)}>
      {showHeaderBar && (
        <DataGridToolbar
          title={title}
          hasAnyGroup={hasAnyGroup}
          allExpanded={allExpanded}
          allCollapsed={allCollapsed}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          exportConfig={
            exportInToolbar && exportConfig
              ? { csv: exportConfig.csv, excel: exportConfig.excel }
              : null
          }
          canExport={canExport}
          isExporting={isExporting}
          onExportCsv={handleExportCsv}
          onExportExcel={handleExportExcel}
        />
      )}

      <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
        {isEmpty ? (
          <div className="p-8">{emptyState}</div>
        ) : (
          <div
            ref={scrollRef}
            className="relative w-full overflow-auto [scrollbar-gutter:stable]"
            style={scrollContainerStyle}
          >
            <table
              className={cn(
                'w-full caption-bottom text-sm',
                singleLineRows && '[&_td]:whitespace-nowrap [&_th]:whitespace-nowrap'
              )}
            >
              <DataGridHeader
                columns={columns}
                bandSegments={bandSegments}
                hasBands={hasBands}
                density={density}
                effectiveStickyHeader={effectiveStickyHeader}
                effectiveSort={effectiveSort}
                onSortClick={handleSortClick}
                headerExport={
                  exportInHeader && exportConfig
                    ? {
                        csv: exportConfig.csv,
                        excel: exportConfig.excel,
                        canExport,
                        isExporting,
                        onExportCsv: handleExportCsv,
                        onExportExcel: handleExportExcel,
                      }
                    : null
                }
              />

              <DataGridBody
                columns={columns}
                renderableRows={renderableRows}
                isLoading={isLoading}
                skeletonRows={skeletonRows}
                summary={summary}
                density={density}
                onRowClick={onRowClick}
                rowKey={rowKey}
                expanded={expanded}
                toggleGroup={toggleGroup}
                renderGroupHeader={grouping?.renderGroupHeader}
                currency={currency}
                isVirtualized={isVirtualized}
                virtualItems={virtualItems}
                virtualPaddingTop={virtualPaddingTop}
                virtualPaddingBottom={virtualPaddingBottom}
              />

              {hasSummary && showGrandTotal && hasRows && sortedData && (
                <GrandTotalRow
                  columns={columns}
                  summary={summary!}
                  rows={sortedData}
                  density={density}
                  currency={currency}
                  sticky={effectiveStickyHeader}
                />
              )}
            </table>
          </div>
        )}
      </div>

      {pagination && !isEmpty && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={pagination.onPageChange}
          onItemsPerPageChange={pagination.onLimitChange}
        />
      )}
    </div>
  );
}
